<script setup lang="ts">
import ArticleBasicInfo from '@/components/common/adp-document/basic-info/article-basic-info.vue';
import PatentBasicInfo from '@/components/common/adp-document/basic-info/patent-basic-info.vue';
import ReportBasicInfo from '@/components/common/adp-document/basic-info/report-basic-info.vue';
import ResearchPaperBasicInfo from '@/components/common/adp-document/basic-info/research-paper-basic-info.vue';
import {
  AdpDocumentBasicInfo,
  isPatent,
  isReport,
  isResearchPaper,
} from '@/types';

interface Props {
  adpDocument: AdpDocumentBasicInfo;
  isNewArticle?: boolean;
  trendCompanyText?: string;
  size?: 'xm' | 'm' | 'l';
  imageVariant?: 'banner' | 'thumbnail';
  isRelatedArticle?: boolean;
  isNarrow?: boolean;
  largeReportThumbnail?: boolean;
}

withDefaults(defineProps<Props>(), {
  isNewArticle: false,
  size: 'xm',
  imageVariant: 'thumbnail',
  isRelatedArticle: false,
  isNarrow: false,
  largeReportThumbnail: false,
});

const emit = defineEmits<{
  'article-link-clicked': [clickedMainTitle?: boolean];
  'article-link-context-menu': [event: Event];
}>();
</script>

<template>
  <ReportBasicInfo
    v-if="isReport(adpDocument)"
    :adp-document="adpDocument"
    :large-thumbnail="largeReportThumbnail"
    :size="size"
    :image-variant="imageVariant"
    :is-narrow="isNarrow"
    :is-related-article="isRelatedArticle"
    @article-link-clicked="emit('article-link-clicked', $event)"
    @article-link-context-menu="emit('article-link-context-menu', $event)"
  />
  <PatentBasicInfo
    v-else-if="isPatent(adpDocument)"
    :adp-document="adpDocument"
    :size="size"
    :image-variant="imageVariant"
    :is-narrow="isNarrow"
    :is-related-article="isRelatedArticle"
    @article-link-clicked="emit('article-link-clicked', $event)"
    @article-link-context-menu="emit('article-link-context-menu', $event)"
  />
  <ResearchPaperBasicInfo
    v-else-if="isResearchPaper(adpDocument)"
    :adp-document="adpDocument"
    :size="size"
    :image-variant="imageVariant"
    :is-narrow="isNarrow"
    :is-related-article="isRelatedArticle"
    @article-link-clicked="emit('article-link-clicked', $event)"
    @article-link-context-menu="emit('article-link-context-menu', $event)"
  />
  <ArticleBasicInfo
    v-else
    :adp-document="adpDocument"
    :size="size"
    :image-variant="imageVariant"
    :is-narrow="isNarrow"
    :is-related-article="isRelatedArticle"
    :is-new-article="isNewArticle"
    :trend-company-text="trendCompanyText"
    @article-link-clicked="emit('article-link-clicked', $event)"
    @article-link-context-menu="emit('article-link-context-menu', $event)"
  >
    <template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
      <slot :name="slotName" v-bind="slotProps ?? {}" />
    </template>
  </ArticleBasicInfo>
</template>
